import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import Button from '@/v2/components/common/Button'
import Modal from '@/v2/components/common/Modal'
import AppointmentForm from '@/v2/modals/appointment/AppointmentEdit/BaseForm'
import AppointmentValid from '@/v2/components/forms/Appointment/validator'
import formatAppointment from '@/v2/components/forms/Appointment/fromForm'
import TimeOffForm from '@/v2/components/forms/TimeOff/Form'
import TimeOffValid from '@/v2/components/forms/TimeOff/validator'
import formatTimeOff from '@/v2/components/forms/TimeOff/fromForm'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'
import { errorsFromJson } from '@/v2/utils/forms'
import { useCreateAppointment } from '@/v2/hooks/useAppointment'

// Deep equality check function
const isEqual = (obj1, obj2) => {
  return JSON.stringify(obj1) === JSON.stringify(obj2)
}
import { useAddTimeOff } from '@/v2/hooks/useTimeOffs'
import { useLoadData } from './hooks/useLoadData'
import { getAppointmentState, getTimeOffState } from './utils'
import { useUser } from '@/v2/hooks/useUser'
import useModals from '@/v2/hooks/useModals'

const Header = styled.div`
  margin-bottom: -8px;
  display: flex;
  column-gap: 20px;

  & > button {
    font-size: 16px;
    font-weight: 300;
    width: 108px;
  }

  & > button.selected {
    font-weight: 500;
  }

  & > button.selected::before {
    content: '';
    position: absolute;
    bottom: -14px;
    width: 100%;
    height: 1px;
    border-bottom: 2px solid black;
  }
`

const CreateEvent = ({
  id,
  start,
  resourceId = '',
  virtual = false,
  patient,
  isOpen,
  onClose,
  onCreate,
}) => {
  const { loading, rooms, doctors, clinic, types } = useLoadData(id)
  const isMounted = useRef(true)
  const { goTo } = useModals()
  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const columnInfo = resourceId.split('-')
  const [tab, setTab] = useState('appointment')
  const [formError, setFormError] = useState({})
  const [isLoading, setIsLoading] = useState(false)

  const { createAppointment } = useCreateAppointment()
  const { addTimeOff } = useAddTimeOff()

  const { onOpenSnackbar } = useSnackbarContext()
  const { user } = useUser()

  const appointmentInitalState = getAppointmentState({
    ...(resourceId ? { [`${columnInfo[0]}Id`]: columnInfo[1] } : {}),
    ...(patient ? { registeredPatient: patient } : {}),
    ...(patient ? { contactMethod: patient.contactMethod } : {}),
    startDate: start,
    virtual,
  })
  const [appointment, setAppointment] = useState(appointmentInitalState)

  const timeOffInitialState = getTimeOffState(clinic, {
    [`${columnInfo[0]}Id`]: columnInfo[1],
    startDate: start,
  })
  const [timeOff, setTimeOff] = useState(timeOffInitialState)

  useEffect(() => {
    if (clinic) {
      setTimeOff(state => ({ ...state, clinicId: clinic }))
    }
  }, [clinic])

  const handleChangeAppointment = items => {
    if (!isMounted.current) return
    const { newPatient, ...values } = items
    setAppointment(prev => ({
      ...prev,
      ...values,
      newPatient: {
        ...prev.newPatient,
        ...newPatient,
      },
    }))
  }

  const handleCreateAppointment = async () => {
    const validErrors = AppointmentValid(appointment)
    if (isMounted.current) {
      setFormError(validErrors || {})
    }

    if (!validErrors) {
      if (isMounted.current) {
        setIsLoading(true)
      }
      try {
        const response = await createAppointment(formatAppointment(appointment))
        if (response.error) {
          const errorMsg =
            typeof response.error === 'object'
              ? Object.values(response.error)[0]
              : response.error
          onOpenSnackbar(
            errorMsg || 'Appointment could not be created. Please try again.',
            'error'
          )
          return
        }

        if (isMounted.current) {
          onOpenSnackbar('Appointment created.')

          if (window.analytics && window.analytics.track) {
            window.analytics.track('New appointment', {
              category: 'Clearworks',
              label: 'New appointment from staff',
              datetime: response.startsAt,
              appointmentType: response.appointmentType?.title,
              room: response.room?.title || 'virtual',
            })
          }

          if (appointment.typeId === '83' || appointment.typeId === '84') {
            if (window.analytics && window.analytics.track) {
              window.analytics.track(
                `Started treatment from ${
                  appointment?.registeredPatient?.fromBookingPage
                    ? 'Booking Page'
                    : 'Clearworks'
                } patient`,
                {
                  category: 'Clearworks',
                  label: 'Started treatment',
                  datetime: response.startsAt,
                }
              )
            }
          }

          onCreate && onCreate()
          onClose()
          // open appointment modal
          goTo(`/v2/appointment/${response.id}`, {
            modalType: 'view',
          })
        }
      } catch (error) {
        console.error('Appointment creation error:', error)
        if (isMounted.current) {
          // Try to parse structured errors
          const asyncErrors = error.response?.errors
            ? errorsFromJson(error.response.errors)
            : errorsFromJson(error.message)

          if (asyncErrors) {
            const { patient, ...otherErrors } = asyncErrors
            const fieldName =
              appointment.patientType === 'new'
                ? 'newPatient'
                : 'registeredPatient'
            setFormError({
              ...otherErrors,
              [fieldName]: {
                ...patient,
                phone: (patient?.phoneNumber || [])[0],
              },
            })
            onOpenSnackbar('Something went wrong!', 'error')
          } else {
            // Show user-friendly error message
            const errorMessage = error.message || 'Something went wrong!'
            onOpenSnackbar(errorMessage, 'error')
          }
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false)
        }
      }
    }
  }

  const handleCreateTimeOff = async () => {
    const validErrors = TimeOffValid(timeOff)
    if (isMounted.current) {
      setFormError(validErrors || {})
    }

    if (!validErrors) {
      if (isMounted.current) {
        setIsLoading(true)
      }
      try {
        const response = await addTimeOff(formatTimeOff(timeOff))
        if (isMounted.current) {
          onOpenSnackbar('Blocked time created.')
          onCreate && onCreate()
          onClose()
        }
      } catch (error) {
        console.error('Time off creation error:', error)
        if (isMounted.current) {
          // Try to parse structured errors
          const asyncErrors = error.response?.errors
            ? errorsFromJson(error.response.errors)
            : errorsFromJson(error.message)

          if (asyncErrors) {
            setFormError(asyncErrors)
            onOpenSnackbar('Something went wrong!', 'error')
          } else {
            // Show user-friendly error message
            const errorMessage = error.message || 'Something went wrong!'
            onOpenSnackbar(errorMessage, 'error')
          }
        }
      } finally {
        if (isMounted.current) {
          setIsLoading(false)
        }
      }
    }
  }

  const handleSubmit = () => {
    if (tab === 'appointment') {
      handleCreateAppointment()
    } else {
      handleCreateTimeOff()
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      loading={isLoading || loading}
      fixedWidth="600px"
      header={
        <Header>
          <Button
            variant="text"
            className={tab === 'appointment' ? 'selected' : ''}
            onClick={() => setTab('appointment')}
          >
            Appointment
          </Button>
          {user?.admin && (
            <Button
              variant="text"
              className={tab !== 'appointment' ? 'selected' : ''}
              onClick={() => setTab('timeoff')}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Blocked time
            </Button>
          )}
        </Header>
      }
      confirmLabel="Create"
      cancelLabel="Cancel"
      onConfirm={handleSubmit}
      onClose={() => {
        if (
          (tab === 'appointment' &&
            !isEqual(appointmentInitalState, appointment)) ||
          (tab === 'timeoff' && !isEqual(timeOffInitialState, timeOff))
        ) {
          const shouldLeave = window.confirm(
            'Are you sure you want to leave without saving changes?'
          )
          if (shouldLeave) onClose()
        } else {
          onClose()
        }
      }}
    >
      {tab === 'appointment' ? (
        <AppointmentForm
          error={formError}
          values={appointment}
          canRemove={true}
          types={types}
          rooms={rooms}
          doctors={doctors}
          onChange={handleChangeAppointment}
        />
      ) : (
        <TimeOffForm
          values={timeOff}
          clinic={clinic}
          rooms={rooms.map(({ id, title }) => ({
            value: id,
            label: title,
          }))}
          doctors={doctors.map(({ id, fullName }) => ({
            value: id,
            label: fullName,
          }))}
          error={formError}
          onChange={value => setTimeOff({ ...timeOff, ...value })}
        />
      )}
    </Modal>
  )
}

export default CreateEvent
